import React, { useState, useRef } from 'react'
import {
  ModifyPlanBundleRow,
  ModifyPlanBundleStyled,
  ModifyPlanNameTitle,
  DowngradePlanError,
} from 'Features/CrossSell/ModifyPlan/ModifyPlanBundle/ModifyPlanBundle.styled'
import {
  CrossSellPricingUpTo,
  CrossSellSliderWrapper,
} from 'Features/CrossSell/CrosSellPricing/CrossSellPricing.styled'
import { botMitigationPlanSizes, paidMarketingPlanSizes } from 'Features/pricing/models/planSizes'
import { Slider } from '@clickcease/cc-client-components-lib'
import { PricingPlansDesktop } from 'Features/pricing/components/PricingPlansDesktop/PricingPlansDesktop'
import { PRODUCT_TYPES } from 'Types/ProductTypes'
import { useTranslation } from 'react-i18next'

import { CurrenciesTypes } from 'Types/Currencies'
import { PlanTypes } from 'Types/PlansTypes'
import { Plan } from 'Features/pricing/types/Plan'
import paidMarketingPlansJson from 'Features/pricing/tabs/PaidMarketing/paidMarketingPlans.json'
import botMitigationPlansJson from 'Features/pricing/tabs/BotMitigation/botMitigationPlans.json'
import { UpToVisits } from 'Features/pricing/components/UpToVisits/UpToVisits'
import { useGetAccountUsage } from 'Features/accountSettings/api/getAccountUsage'
import { getCurrentVisitsCount, geteMinimumPlanVisitsByCurrentUsage } from './utils'

type ModifyPlanBundleProps = {
  currency: CurrenciesTypes
  pmNumOfVisits: number
  bmNumOfVisits: number
  pmPlanType: PlanTypes
  bmPlanType: PlanTypes
  plans: Array<Plan>
  setPmPlanType: (plan: PlanTypes) => void
  setBmPlanType: (plan: PlanTypes) => void
  setPmNumOfVisits: (value: number) => void
  setBmNumOfVisits: (value: number) => void
  source: string
}

enum Products {
  PaidMarketing = 'pm',
  BotMitigation = 'bm',
}

enum ProductsFullName {
  PaidMarketing = 'PaidMarketing',
  BotMitigation = 'BotZapping',
}

type ProductConfig = {
  fullName: string
  planSizes: number[]
  setOverUse: (value: boolean) => void
  setKey: (value: number) => void
  currentUsageValue: { current: number }
}

export const ModifyPlanBundle = ({
  currency,
  pmNumOfVisits,
  pmPlanType,
  setPmPlanType,
  plans,
  bmPlanType,
  bmNumOfVisits,
  setPmNumOfVisits,
  setBmNumOfVisits,
  setBmPlanType,
  source,
}: ModifyPlanBundleProps) => {
  console.log('ModifyPlanBundle.tsx loaded')

  const { t } = useTranslation('pricing')
  const { data: accountUsage } = useGetAccountUsage()
  const [pmKey, setPmKey] = useState(Date.now())
  const [bmKey, setBmKey] = useState(Date.now())
  const [overUsePm, setOverUsePm] = useState(false)
  const [overUseBm, setOverUseBm] = useState(false)
  const pmCurrentUsageValue = useRef(pmNumOfVisits * 1000)
  const bmCurrentUsageValue = useRef(bmNumOfVisits * 1000)

  // All required data fields for slider blocking when downgrading plan per product
  const PRODUCT_CONFIGS: Record<string, ProductConfig> = {
    [Products.PaidMarketing]: {
      fullName: ProductsFullName.PaidMarketing,
      planSizes: paidMarketingPlanSizes,
      setOverUse: setOverUsePm,
      setKey: setPmKey,
      currentUsageValue: pmCurrentUsageValue,
    },
    [Products.BotMitigation]: {
      fullName: ProductsFullName.BotMitigation,
      planSizes: botMitigationPlanSizes,
      setOverUse: setOverUseBm,
      setKey: setBmKey,
      currentUsageValue: bmCurrentUsageValue,
    },
  }

  const calculateNewSliderValue = (
    newValue: number,
    previousValue: number,
    product: string
  ): number => {
    console.log('inside calculateNewSliderValue')

    const newValueNormalized = newValue * 1000
    const config = PRODUCT_CONFIGS[product]
    const currentVisitsCount: number | undefined = getCurrentVisitsCount(
      accountUsage,
      config.fullName
    )
    const minimumPlan = geteMinimumPlanVisitsByCurrentUsage(
      currentVisitsCount || 0,
      config.planSizes
    )
    const minimumSlide = Math.min(minimumPlan, config.currentUsageValue.current)

    console.log('newValueNormalized', newValueNormalized)
    console.log('currentVisitsCount', newValueNormalized)
    console.log('minimumPlan', newValueNormalized)
    console.log('minimumSlide', newValueNormalized)

    if (newValueNormalized < minimumSlide) {
      console.log('Blocking slide!')

      config.setOverUse(true)
      config.setKey(Date.now()) // force re-render slider to block slide
      return previousValue
    }

    config.setOverUse(false)
    return newValue
  }

  return (
    <ModifyPlanBundleStyled>
      <ModifyPlanNameTitle>{t('modify_plan.paid_marketing')}</ModifyPlanNameTitle>
      <CrossSellPricingUpTo>
        <UpToVisits numOfVisits={pmNumOfVisits} maxVisits={1000} />
      </CrossSellPricingUpTo>
      <ModifyPlanBundleRow css={{ marginBottom: '50px' }}>
        <CrossSellSliderWrapper>
          <Slider
            key={pmKey}
            width={'520px'}
            min={paidMarketingPlanSizes[0]}
            max={paidMarketingPlanSizes[paidMarketingPlanSizes.length - 1]}
            previousValue={pmNumOfVisits}
            value={pmNumOfVisits}
            steps={paidMarketingPlanSizes}
            minUnit={'k'}
            maxUnit={t('bundles.million')}
            maxText={t('bundles.million_number', {
              number: 1,
            })}
            onChange={(newValue) => {
              console.log('pm slider onchange invoked')
              const determinedSliderValue = calculateNewSliderValue(
                newValue as number,
                pmNumOfVisits,
                'pm'
              )
              setPmNumOfVisits(determinedSliderValue)
            }}
          />
          {overUsePm ? <DowngradePlanError>{t('bundles.over_use')}</DowngradePlanError> : null}
        </CrossSellSliderWrapper>
        <PricingPlansDesktop
          numOfVisits={pmNumOfVisits}
          crossSell={true}
          plansDb={plans || []}
          setSelectedPlan={setPmPlanType}
          selectedPlan={pmPlanType}
          plansJson={paidMarketingPlansJson}
          productName={PRODUCT_TYPES.PAID_MARKETING}
          currency={currency}
          source={source}
        />
      </ModifyPlanBundleRow>
      <ModifyPlanBundleRow>
        <ModifyPlanNameTitle>{t('modify_plan.bot_mitigation')}</ModifyPlanNameTitle>
        <CrossSellPricingUpTo>
          <UpToVisits numOfVisits={bmNumOfVisits} maxVisits={1500} />
        </CrossSellPricingUpTo>
        <CrossSellSliderWrapper>
          <Slider
            key={bmKey}
            width={'520px'}
            min={botMitigationPlanSizes[0]}
            max={botMitigationPlanSizes[botMitigationPlanSizes.length - 1]}
            previousValue={bmNumOfVisits}
            value={bmNumOfVisits}
            steps={botMitigationPlanSizes}
            minUnit={'k'}
            maxUnit={t('bundles.million')}
            maxText={t('bundles.million_number', {
              number: 1.5,
            })}
            onChange={(newValue) => {
              console.log('bm slider onchange invoked')
              const determinedSliderValue = calculateNewSliderValue(
                newValue as number,
                bmNumOfVisits,
                'bm'
              )
              setBmNumOfVisits(determinedSliderValue)
            }}
          />
          {overUseBm ? <DowngradePlanError>{t('bundles.over_use')}</DowngradePlanError> : null}
        </CrossSellSliderWrapper>
        <PricingPlansDesktop
          numOfVisits={bmNumOfVisits}
          crossSell={true}
          plansDb={plans || []}
          setSelectedPlan={setBmPlanType}
          selectedPlan={bmPlanType}
          plansJson={botMitigationPlansJson}
          productName={PRODUCT_TYPES.BOT_ZAPPING}
          currency={currency}
          source={source}
        />
      </ModifyPlanBundleRow>
    </ModifyPlanBundleStyled>
  )
}
